/**
 * FileCell Component
 * 
 * Renders Parse File values with:
 * - Pill display with file icon
 * - Truncated filename
 * - Direct download link
 * - Different icon for images vs other files
 */

import { CellRendererProps, ParseFile } from '../../../../../types/AppPanel';

const MAX_NAME_LENGTH = 20;

export default function FileCell({ 
  value 
}: CellRendererProps<ParseFile | null | undefined>) {
  // Handle null/undefined
  if (value === null) {
    return <span className="text-white/30 italic">(null)</span>;
  }
  if (value === undefined) {
    return <span className="text-white/30 italic">(undefined)</span>;
  }

  // Handle different file value formats
  let name: string;
  let url: string;

  if (typeof value === 'object' && value !== null) {
    name = value.name || 'Unnamed file';
    url = value.url || '';
  } else {
    return <span className="text-white/30 italic">(invalid file)</span>;
  }

  // Truncate name for display
  const displayName = name.length > MAX_NAME_LENGTH 
    ? name.slice(0, MAX_NAME_LENGTH - 3) + '...' 
    : name;

  // Detect file type for icon
  const ext = name.split('.').pop()?.toLowerCase() || '';
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);

  if (!url) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full 
                       bg-white/10 text-white/50 text-xs font-medium">
        {isImage ? <ImageIcon /> : <FileIcon />}
        {displayName}
      </span>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full 
                 bg-purple-500/20 text-purple-400 text-xs font-medium
                 hover:bg-purple-500/30 transition-colors"
      title={name}
    >
      {isImage ? <ImageIcon /> : <FileIcon />}
      {displayName}
    </a>
  );
}

function ImageIcon() {
  return (
    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

